<!--
  * Path: packages/frontend/src/features/browser/components/BrowserContent.vue
  * Task: Render dynamic form fields logically grouped into FormSections.
  * Update: Fixed bug where complex n:n relations (CAIRO, Predecessors) were incorrectly hidden in read mode by improving the isFieldEmpty logic.
-->
<template>
  <div class="fill-height bg-surface">
    <!-- Friendly UX empty state when no entity is currently selected in the URL -->
    <v-container v-if="!activeEntity" class="fill-height">
      <v-row align="center" justify="center">
        <v-col class="text-center text-medium-emphasis">
          <v-icon size="64" class="mb-4 opacity-50" icon="mdi-text-box-search-outline"></v-icon>
          <h2 class="text-h5 font-weight-bold mb-2">No Entity Selected</h2>
          <p class="text-body-1">Please select an item from the list on the left to view and edit its details.</p>
        </v-col>
      </v-row>
    </v-container>

    <!-- Fallback if no schema definition exists for the selected entity type -->
    <v-container v-else-if="filteredCombinedSchema.length === 0" class="fill-height">
      <v-row align="center" justify="center">
        <v-col class="text-center text-warning">
          <v-icon size="48" class="mb-2" icon="mdi-alert-circle-outline"></v-icon>
          <p>No form definition found for entity type <strong>"{{ activeEntity?.entityType }}"</strong>.</p>
        </v-col>
      </v-row>
    </v-container>

    <!-- Form rendering using dynamically grouped schemas -->
    <v-form v-else ref="entityForm" @submit.prevent="onSubmit">
      <template v-for="(group, index) in groupedSchema" :key="group.title + '-' + index">
        <!-- Render the FormSection only if it has visible fields -->
        <FormSection
          v-if="hasVisibleFieldsInGroup(group)"
          :title="group.title"
          :title-class="canEditWorkspace ? 'ms-10' : 'ps-2'"
        >
          <template v-for="item in group.items" :key="item._kind + '-' + item.id">
            <!-- Conditionally render the field -->
            <div v-show="isFieldVisible(item)">
              <!-- ========================================== -->
              <!-- 1. STANDARD FIELDS                           -->
              <!-- ========================================== -->
              <template v-if="item._kind === 'field'">
                <!-- Type: LABEL -->
                <div v-if="item.type === 'label'">
                  <div class="text-h6 mt-2 mb-2 d-flex align-center text-high-emphasis">
                    {{ item.name }}
                    <v-tooltip v-if="item.info" location="end">
                      <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-information-outline" variant="text" size="small" class="ms-2" @click="showInfo(item)"></v-btn>
                      </template>
                      <span>{{ item.info }}</span>
                    </v-tooltip>
                  </div>
                </div>

                <!-- Type: ORDER -->
                <Order v-else-if="item.type === 'order'" :field="item" :context="activeEntity?.entityType || ''" @show-info="showInfo(item)" />

                <!-- Type: LINK -->
                <Link v-else-if="item.type === 'link'" :field="item" @show-info="showInfo(item)" />

                <!-- Type: COLOR -->
                <Color v-else-if="item.type === 'color'" :field="item" @click:info="showInfo(item)" />

                <!-- Type: RATING -->
                <Rating v-else-if="item.type === 'rating'" :field="item" :error-messages="errors[item.id]" @show-info="showInfo(item)" />

                <!-- Type: ICON -->
                <Icon v-else-if="item.type === 'icon'" :field="item" @click:info="showInfo(item)" />

                <!-- Type: MULTISELECT (Block-Level Editing) -->
                <MultiselectBlock v-else-if="item.type === 'multiselect'" :field="item" @show-info="showInfo(item)" />

                <!-- Type: DATETIME -->
                <DateTime v-else-if="item.type === 'datetime'" :field="item" :error-messages="errors[item.id]" @show-info="showInfo(item)" />

                <!-- Type: NUMBER (Integer, Float, Currency) -->
                <NumberField v-else-if="['integer', 'float', 'currency'].includes(item.type)" :field="item" :error-messages="errors[item.id]" @show-info="showInfo(item)" />

                <!-- Type: GENERIC INPUTS -->
                <StandardInput
                  v-else-if="['text', 'markdown', 'select', 'simple-select'].includes(item.type)"
                  :field="item"
                  :error-messages="errors[item.id]"
                  @show-info="showInfo(item)"
                />
              </template>

              <!-- ========================================== -->
              <!-- 2. RELATIONS (Outgoing & Bidirectional)      -->
              <!-- ========================================== -->
              <template v-else-if="item._kind === 'relation'">
                <component
                  :is="resolveRelationComponent(item.uiPresentation, item.isMultiple)"
                  :field="item.asField"
                  :error-messages="errors[item.asField.id]"
                  @show-info="showInfo(item.asField)"
                />
              </template>

              <!-- ========================================== -->
              <!-- 3. INVERSE RELATIONS (Incoming)              -->
              <!-- ========================================== -->
              <template v-else-if="item._kind === 'inverse_relation'">
                <component
                  :is="resolveRelationComponent(item.uiPresentation, true)"
                  :field="item.asField"
                  :inverse-columns="item.tableColumns"
                  @show-info="showInfo(item.asField)"
                />
              </template>
            </div>
          </template>
        </FormSection>
      </template>

      <!-- Validation Error Notice -->
      <v-alert v-if="Object.keys(errors).length > 0" type="error" variant="tonal" class="mb-4">
        Please resolve the errors in the marked fields before saving.
      </v-alert>

      <!-- Bottom padding to prevent the form from being hidden behind sticky footers -->
      <div style="height: 64px;"></div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { usePermissions } from '@/core/composables/usePermissions';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { FormContextKey } from '@/core/keys';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';

import Timeline from '@/core/components/forms/timeline/Timeline.vue';
import Relations from '@/core/components/forms/relations/Relations.vue';
import Order from '@/core/components/forms/Order.vue';
import StandardInput from '@/core/components/forms/StandardInput.vue';
import CairoMatrix from '@/core/components/forms/cairoMatrix/CairoMatrix.vue';
import Mindmap from '@/core/components/forms/mindmap/Mindmap.vue';
import Link from '@/core/components/forms/link/Link.vue';
import MultiselectBlock from '@/core/components/forms/MultiselectBlock.vue';
import Color from '@/core/components/forms/color/Color.vue';
import Icon from '@/core/components/forms/icon/Icon.vue';
import Rating from '@/core/components/forms/Rating.vue';
import NumberField from '@/core/components/forms/number/NumberField.vue';
import DateTime from '@/core/components/forms/datetime/DateTime.vue';
import FormSection from '@/core/components/common/FormSection.vue';

import { useEntitySchema } from '@/features/browser/composables/useEntitySchema';
import type { VForm } from 'vuetify/components';
import type { Entity } from '@/core/types/editor';

interface InfoPayload {
  title: string;
  text?: string;
}

interface SchemaGroup {
  title: string;
  items: any[];
  _rank?: number;
}

const emit = defineEmits<{
  (e: 'show-info', payload: InfoPayload): void;
}>();

const entityForm = ref<InstanceType<typeof VForm> | null>(null);
const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const authStore = useAuthStore();
const projectStore = useProjectStore();
const { canEditWorkspace } = usePermissions();
const { saveEntity } = useEntityOperations();
const formContext = inject(FormContextKey, null);

const activeEntity = computed<Entity | null>(() => {
  if (formContext) return formContext.entity.value;
  return workspaceStore.selectedEntity;
});

const isLocked = computed<boolean>(() => {
  if (formContext) return formContext.isLocked.value;
  return workspaceStore.isLocked;
});

// Determine if we are running in the Preview Modal context
const isPreviewContext = computed<boolean>(() => {
  return workspaceStore.isPreviewModalOpen && formContext?.entityId.value === workspaceStore.previewEntityId;
});

const { filteredCombinedSchema } = useEntitySchema(activeEntity, isLocked);

// --- FIELD VISIBILITY LOGIC ---

// Check if a field has any assigned value
const isFieldEmpty = (item: any): boolean => {
  if (!activeEntity.value) return true;

  // --- Special Logic for Complex Relations (Matrix, Mindmap) ---
  if (item._kind === 'relation' || item._kind === 'inverse_relation') {
    // 1. CAIRO / RACI (Matrix)
    if (item.id === 'cairo' || item.id === 'inverse_cairo') {
      const raci = activeEntity.value.raci;
      if (!raci || typeof raci !== 'object') return true;
      // Check if ANY of the raci sub-arrays have content
      return !Object.values(raci).some((arr: any) => Array.isArray(arr) && arr.length > 0);
    }

    // 2. Predecessors (Mindmap / Sequence)
    if (item.id === 'predecessors' || item.id === 'inverse_predecessors') {
      const hasPreds = Array.isArray(activeEntity.value.predecessorIds) && activeEntity.value.predecessorIds.length > 0;
      const hasSuccs = Array.isArray(activeEntity.value.successorIds) && activeEntity.value.successorIds.length > 0;
      // Mindmaps display both incoming and outgoing, so if either exists, it's not empty
      return !(hasPreds || hasSuccs);
    }
  }

  // --- Standard Logic for normal fields ---
  let val: any;
  if (item._kind === 'field') {
    val = activeEntity.value[item.id];
  } else if (item._kind === 'relation' || item._kind === 'inverse_relation') {
    val = activeEntity.value[item.asField.id];
  }

  // Handle Arrays (Multiselect, Relations)
  if (Array.isArray(val)) {
    return val.length === 0;
  }
  // Handle Objects (Links)
  if (typeof val === 'object' && val !== null) {
    if(val.url !== undefined) {
      return !val.url;
    }
    return Object.keys(val).length === 0;
  }

  // Handle strings, numbers, booleans
  return val === null || val === undefined || val === '';
};

// Main visibility check
const isFieldVisible = (item: any): boolean => {
  // 1. Always show if we are in the dedicated preview modal
  if (isPreviewContext.value) {
    return true;
  }
  // 2. Always show if the form is unlocked (editing mode)
  if (!isLocked.value) {
    return true;
  }
  // 3. In locked mode (read mode) in middle or right pane, hide if empty
  return !isFieldEmpty(item);
};

// Check if an entire section group should be shown
const hasVisibleFieldsInGroup = (group: SchemaGroup): boolean => {
  return group.items.some(item => isFieldVisible(item));
};

// --- DYNAMIC COMPONENT RESOLVER ---
const resolveRelationComponent = (uiPresentation: string, isMultiple: boolean) => {
  switch (uiPresentation) {
    case 'mindmap': return Mindmap;
    case 'matrix': return CairoMatrix;
    case 'timeline': return Timeline;
    case 'multiselect': return isMultiple ? MultiselectBlock : Relations;
    default: return isMultiple ? MultiselectBlock : Relations;
  }
};

// --- ZOD & VEE-VALIDATE INTEGRATION ---
const dynamicValidationSchema = computed(() => {
  const shape: Record<string, z.ZodTypeAny> = {};
  const projectId = projectStore.loadedProject?.value;
  const fieldSettings = (projectId && projectStore.projectsMetadata?.[projectId]?.fieldSettings) || {};

  filteredCombinedSchema.value.forEach((item: any) => {
    if (item._kind === 'field') {
      let validator: z.ZodTypeAny = z.any();
      const isRequired = item.isRequired || fieldSettings[item.id]?.isRequired || false;

      if (item.type === 'text') {
        validator = (item.readonly || !isRequired)
          ? z.string().optional().nullable()
          : z.string().min(1, `${item.name || 'This field'} cannot be empty.`);
      } else if (item.type === 'multiselect') {
        validator = isRequired
          ? z.array(z.string()).min(1, `${item.name || 'This field'} requires at least one entry.`)
          : z.array(z.string()).optional().nullable();
      } else if (item.type === 'rating') {
        validator = isRequired
          ? z.number().min(1, `${item.name || 'This field'} is required.`).max(5)
          : z.number().min(0).max(5).optional().nullable();
      } else if (['integer', 'float', 'currency'].includes(item.type)) {
        validator = isRequired
          ? z.coerce.number({ invalid_type_error: `${item.name || 'This field'} must be a number.` })
          : z.coerce.number({ invalid_type_error: `${item.name || 'This field'} must be a number.` }).optional().nullable();
      } else if (item.type === 'datetime') {
        validator = isRequired
          ? z.string().min(1, `${item.name || 'This field'} cannot be empty.`)
          : z.string().optional().nullable();
      } else {
        validator = isRequired
          ? z.any().refine(val => val !== undefined && val !== null && val !== '', { message: `${item.name || 'This field'} cannot be empty.` })
          : z.any().optional().nullable();
      }
      shape[item.id] = validator;
    } else if (item._kind === 'relation') {
      shape[item.asField.id] = item.isMultiple
        ? z.array(z.string()).optional().nullable()
        : z.string().optional().nullable();
    }
  });

  return toTypedSchema(z.object(shape));
});

const { errors, validate, handleSubmit } = useForm({
  validationSchema: dynamicValidationSchema,
  initialValues: activeEntity.value || {}
});

watch(() => activeEntity.value, async (newVal) => {
  if (newVal && !isLocked.value) {
    await validate();
  }
}, { deep: true });

const onSubmit = handleSubmit((values) => {
  const isDraft = formContext?.isDraft || false;
  saveEntity(formContext?.isSecondary || false, isDraft);
});

// --- SCHEMA GROUPING LOGIC ---
const groupedSchema = computed<SchemaGroup[]>(() => {
  const groups: SchemaGroup[] = [];
  const groupMap = new Map<string, any[]>();
  const formSections = authStore.appData?.formSections || [];

  filteredCombinedSchema.value.forEach((item: any) => {
    const rawSectionId = item.sectionId || item.section || 'general';
    const sectionDef = formSections.find((s: any) => s.id === rawSectionId);

    let sectionTitle = sectionDef ? (sectionDef.name_en || sectionDef.name_ger || rawSectionId) : 'General Details';

    if (!sectionDef) {
      if (item._kind === 'relation') sectionTitle = 'Connections & References';
      else if (item._kind === 'inverse_relation') sectionTitle = 'Incoming Links';
    }

    if (!groupMap.has(sectionTitle)) {
      const newItemsArray: any[] = [];
      groupMap.set(sectionTitle, newItemsArray);
      groups.push({
        title: sectionTitle,
        items: newItemsArray,
        _rank: sectionDef ? (sectionDef.formRank || 999) : 999
      });
    }
    groupMap.get(sectionTitle)!.push(item);
  });

  groups.sort((a, b) => (a._rank || 999) - (b._rank || 999));
  return groups;
});

const showInfo = (fieldOrPayload: any): void => {
  const infoData = fieldOrPayload.info ? fieldOrPayload : fieldOrPayload;
  if (infoData.info || infoData.text) {
    emit('show-info', {
      title: infoData.name || infoData.title || 'Information',
      text: infoData.info || infoData.text
    });
  }
};
</script>
